import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    shipment: any;
}

type Props = OwnProps;

export interface AdiOrderDetailsOrderTotalDisplayStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    subtotalLabelGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValueGridItem?: GridItemProps;
    subtotalValue?: TypographyProps;
    taxLabelGridItem?: GridItemProps;
    taxLabel?: TypographyProps;
    taxValueGridItem?: GridItemProps;
    taxValue?: TypographyProps;
    shippingAndHandlingLabelGridItem?: GridItemProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValueGridItem?: GridItemProps;
    shippingAndHandlingValue?: TypographyProps;
    totalLabelGridItem?: GridItemProps;
    totalLabel?: TypographyProps;
    totalValueGridItem?: GridItemProps;
    totalValue?: TypographyProps;
}

export const adiOrderDetailsOrderTotalDisplayStyles: AdiOrderDetailsOrderTotalDisplayStyles = {
    totalValueGridItem: {
        css: css`
            padding-top: 13px;
            border-top: 1px solid ${getColor("common.border")};
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    totalValue: {
        css: css`
            margin-left: auto;
            font-size: 20px;
            font-weight: 900;
            @media only screen and (max-width: 1024px) {
                font-size: 24px;
            }
            @media only screen and (max-width: 576px) {
                color: ${ADIColors.darkBlue1};
            }
            @media print {
                font-size: 14px;
                padding-left: 0px;
                padding-right: 0px;
                color: ${ADIColors.black};
            }
        `,
    },
    totalLabelGridItem: {
        width: 6,
        css: css`
        padding-top: 13px;
        border-top: 1px solid ${getColor("common.border")};
        @media print{
            font-size: 14px;
            color: ${ADIColors.black};
        }
        `
    },
    totalLabel: {
        css: css`
            font-weight: bold;
            @media only screen and (max-width: 1024px) {
                font-size: 20px;
            }
            @media only screen and (max-width: 576px) {
                color: ${ADIColors.darkBlue1};
            }
        `
    },
    container: {
        gap: 10,
        css: css`
            border: 1px solid ${getColor("common.border")};
            padding: 16px 16px 12px;
            border-radius: 3px;
            max-width: 312px !important;
            width: 100%;
            min-height: 158px;
            margin: 0 0 20px;
            font-size: 16px;
            color: ${getColor("text.main")};
            @media only screen and (max-width: 1024px) {
                border: 0;
                padding: 0;
                margin-bottom: 0px;
                max-width: unset !important;
                width: 335px;
                min-height: auto;
            }
            @media only screen and (max-width: 576px) {
                >${GridOffset}{
                    width: 100% !important;
                }
            }
            @media print{
                padding: 12px;
                max-width: 100% !important;
            }
        `,
    },
    subtotalLabelGridItem: {
        width: 6,
        css: css`
        @media print{
            font-size: 14px;
            color: ${ADIColors.black};
        }
        `
    },
    subtotalValueGridItem: {
        css: css`
            white-space: nowrap;
            @media print {
                text-align: right;
                font-size: 14px;
                color: ${ADIColors.black};
            }
        `,
        width: 6,
    },
    subtotalValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxLabelGridItem: {
        width: 6,
        css: css`
        @media print{
            font-size: 14px;
            color: ${ADIColors.black};
        }
        `
    },
    taxValueGridItem: {
        css: css`
            @media print {
                text-align: right;
                font-size: 14px;
                color: ${ADIColors.black};
            }
        `,
        width: 6,
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingLabelGridItem: {
        width: 6,
        css: css`
        @media print{
            font-size: 14px;
            color: ${ADIColors.black};
        }
        `
    },
    shippingAndHandlingValueGridItem: {
        css: css`
            @media print {
                text-align: right;
                font-size: 14px;
                color: ${ADIColors.black};
            }
        `,
        width: 6,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
};

const styles = adiOrderDetailsOrderTotalDisplayStyles;

const ADIOrderDetailsTotalDisplay: FC<Props> = ({ shipment }) => {
    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.subtotalLabelGridItem}>
                    <Typography {...styles.subtotalLabel}>
                        {translate("Material Subtotal")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.subtotalValueGridItem}>
                    <Typography {...styles.subtotalValue}>{shipment?.productTotalDisplay}</Typography>
                </GridItem>
                {/* Ecofee Implementation ----P21 Changes */}
                {shipment?.totalEcoFeeDisplay != '$0.00' &&(
                    <>
                        <GridItem {...styles.subtotalValueGridItem}>
                            <Typography {...styles.subtotalLabel}>{translate("Eco Fee")}</Typography>
                        </GridItem>
                        <GridItem {...styles.subtotalValueGridItem}>
                            <Typography {...styles.subtotalValue}>{shipment?.totalEcoFeeDisplay}</Typography>
                        </GridItem>
                    </>
                )}
                {shipment?.totalSpecialHandlingFeeDisplay != '$0.00' &&(
                    <>
                        <GridItem {...styles.subtotalValueGridItem}>
                            <Typography {...styles.subtotalLabel}>{translate("Special Handling Fee")}</Typography>
                        </GridItem>
                        <GridItem {...styles.subtotalValueGridItem}>
                            <Typography {...styles.subtotalValue}>{shipment?.totalSpecialHandlingFeeDisplay}</Typography>
                        </GridItem>
                    </>
                )}
                
                  {/* {shipment?.statusType != 'OPEN' && (   */}
                {(shipment?.suStatus == 'Shipped' ||
                shipment?.suStatus == 'Picked Up' || 
                shipment?.suStatus == 'Ready for Pickup') && (
                
                    <>
                        <GridItem {...styles.taxLabelGridItem}>
                            <Typography {...styles.taxLabel}>{translate("Tax")}</Typography>
                        </GridItem>
                        <GridItem {...styles.taxValueGridItem}>
                            <Typography {...styles.taxValue}>{shipment?.taxDisplay}</Typography>
                        </GridItem>
                    </>
                )}
                  {(shipment?.suStatus == 'Shipped' ||
                shipment?.suStatus == 'Picked Up' || 
                shipment?.suStatus == 'Ready for Pickup') && (
                   <>
                  {/* {shipment?.statusType != 'OPEN' && ( */}
                 
                        <GridItem {...styles.shippingAndHandlingLabelGridItem}>
                            <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping")}</Typography>
                        </GridItem>
                        <GridItem {...styles.shippingAndHandlingValueGridItem}>
                            <Typography {...styles.shippingAndHandlingValue}>{shipment?.shippingDisplay}</Typography>
                        </GridItem>
                    </>
                )}
                {/* {shipment?.statusType != 'OPEN' && ( */}
                {(shipment?.suStatus == 'Shipped' ||
                shipment?.suStatus == 'Picked Up' || 
                shipment?.suStatus == 'Ready for Pickup') && (
                    <>
                        <GridItem {...styles.totalLabelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.totalValueGridItem}>
                            <Typography {...styles.totalValue}>{shipment?.orderTotalDisplay}</Typography>
                        </GridItem>
                    </>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

export default ADIOrderDetailsTotalDisplay;
